/**
 * Modern Floating Status Card Component
 * High-tech glassmorphism design with depth and smooth interactions
 */

import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus, Percent, LucideIcon } from 'lucide-react';

type TrendType = 'up' | 'down' | 'neutral';
type ValueType = 'currency' | 'percentage' | 'custom';
type GradientColor = 'blue' | 'green' | 'orange' | 'purple' | 'emerald' | 'cyan' | 'red' | 'rose';

// Color gradient mapping for background effects
const gradientColorMap: Record<GradientColor, { overlay: string; shine: string }> = {
  blue: {
    overlay: 'from-blue-500/5 dark:from-blue-500/10',
    shine: 'from-blue-400/20',
  },
  green: {
    overlay: 'from-green-500/5 dark:from-green-500/10',
    shine: 'from-green-400/20',
  },
  orange: {
    overlay: 'from-orange-500/5 dark:from-orange-500/10',
    shine: 'from-orange-400/20',
  },
  purple: {
    overlay: 'from-purple-500/5 dark:from-purple-500/10',
    shine: 'from-purple-400/20',
  },
  emerald: {
    overlay: 'from-emerald-500/5 dark:from-emerald-500/10',
    shine: 'from-emerald-400/20',
  },
  cyan: {
    overlay: 'from-cyan-500/5 dark:from-cyan-500/10',
    shine: 'from-cyan-400/20',
  },
  red: {
    overlay: 'from-red-500/5 dark:from-red-500/10',
    shine: 'from-red-400/20',
  },
  rose: {
    overlay: 'from-rose-500/5 dark:from-rose-500/10',
    shine: 'from-rose-400/20',
  },
};

interface StatusCardProps {
  title: string;
  value: string | number;
  subtext: string;
  trend: TrendType;
  type?: ValueType;
  icon?: LucideIcon;
  iconColor?: string;
  iconBgColor?: string;
  gradientColor?: GradientColor;
  className?: string;
}

export function StatusCard({
  title,
  value,
  subtext,
  trend,
  type = 'currency',
  icon: CustomIcon,
  iconColor,
  iconBgColor,
  gradientColor,
  className,
}: StatusCardProps) {
  // Get gradient colors if specified
  const gradientColors = gradientColor ? gradientColorMap[gradientColor] : null;
  // Determine trend colors and icon
  const trendConfig = {
    up: {
      icon: TrendingUp,
      iconBg: 'bg-emerald-500/10',
      iconColor: 'text-emerald-500',
      textColor: 'text-emerald-600 dark:text-emerald-400',
      glow: 'shadow-emerald-500/10',
    },
    down: {
      icon: TrendingDown,
      iconBg: 'bg-rose-500/10',
      iconColor: 'text-rose-500',
      textColor: 'text-rose-600 dark:text-rose-400',
      glow: 'shadow-rose-500/10',
    },
    neutral: {
      icon: Minus,
      iconBg: 'bg-slate-500/10',
      iconColor: 'text-slate-500',
      textColor: 'text-slate-600 dark:text-slate-400',
      glow: 'shadow-slate-500/10',
    },
  };

  const config = trendConfig[trend];
  const TrendIcon = config.icon;

  return (
    <div
      className={cn(
        // Base glass morphism
        'group relative overflow-hidden rounded-2xl',
        'bg-gradient-to-br from-white/80 to-white/40',
        'dark:from-slate-900/80 dark:to-slate-800/40',
        'backdrop-blur-xl border border-white/20 dark:border-white/10',

        // Multi-layer shadows for floating effect
        'shadow-[0_8px_32px_rgba(0,0,0,0.06),0_2px_8px_rgba(0,0,0,0.04)]',
        'dark:shadow-[0_8px_32px_rgba(0,0,0,0.3),0_2px_8px_rgba(0,0,0,0.2)]',

        // Hover state - lift up with enhanced shadow
        'transition-all duration-500 ease-out',
        'hover:-translate-y-2',
        'hover:shadow-[0_20px_60px_rgba(0,0,0,0.08),0_8px_24px_rgba(0,0,0,0.06)]',
        'dark:hover:shadow-[0_20px_60px_rgba(0,0,0,0.4),0_8px_24px_rgba(0,0,0,0.3)]',

        // Subtle glow on hover
        `hover:${config.glow}`,

        className
      )}
    >
      {/* Gradient overlay for depth */}
      <div className={cn(
        "absolute inset-0 bg-gradient-to-br to-transparent pointer-events-none",
        gradientColors ? gradientColors.overlay : "from-white/5"
      )} />

      {/* Content container */}
      <div className="relative p-6">
        {/* Header with title and icon */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <p className="text-sm font-medium text-muted-foreground/80 uppercase tracking-wide">
              {title}
            </p>
          </div>

          {/* Floating icon badge */}
          <div
            className={cn(
              'flex items-center justify-center w-10 h-10 rounded-xl',
              'transition-all duration-300',
              'group-hover:scale-110 group-hover:rotate-3',
              iconBgColor || config.iconBg,
              'backdrop-blur-sm'
            )}
          >
            {type === 'custom' && CustomIcon ? (
              <CustomIcon className={cn('h-5 w-5', iconColor || config.iconColor)} />
            ) : type === 'currency' ? (
              <span className={cn('text-lg font-bold', iconColor || config.iconColor)}>₪</span>
            ) : (
              <Percent className={cn('h-5 w-5', iconColor || config.iconColor)} />
            )}
          </div>
        </div>

        {/* Value display */}
        <div className="mb-3">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold tracking-tight text-foreground">
              {value}
            </span>
          </div>
        </div>

        {/* Trend indicator and subtext */}
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex items-center gap-1 px-2.5 py-1 rounded-full',
              'transition-all duration-300',
              'group-hover:scale-105',
              config.iconBg,
              config.textColor
            )}
          >
            <TrendIcon className="h-3.5 w-3.5" />
            <span className="text-xs font-semibold">{subtext}</span>
          </div>
        </div>
      </div>

      {/* Shine effect on hover */}
      <div
        className={cn(
          'absolute -top-24 -right-24 w-48 h-48 rounded-full',
          'bg-gradient-to-br to-transparent',
          gradientColors ? gradientColors.shine : 'from-white/30',
          'opacity-0 group-hover:opacity-100',
          'transition-opacity duration-700',
          'pointer-events-none blur-2xl'
        )}
      />
    </div>
  );
}
